import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MultiSelect } from 'primeng/multiselect';
import { debounceTime, Subject } from 'rxjs';

// Components
import { BaseComponent } from './base.component';

// Services
import { Logger } from 'src/app/@core/services/logger/logger.service';

// Utils
import { Filter } from '../../utilities/models/filter';

const LOGGER = new Logger('TableFiltersBaseComponent');

/*----------------------------------------------------------------------------------------
 * Interface de configuration pour un filtre de plage de dates
 ----------------------------------------------------------------------------------------*/
export interface DateRangeFilterConfig {
    property: string;                                      // Nom de la propriété pour le filtre (ex: 'issueDate', 'dueDate')
    getRange: () => [Date | null, Date | null] | null;    // Fonction pour obtenir la plage de dates
    setRange: (range: [Date | null, Date | null] | null) => void; // Fonction pour définir la plage de dates
}

/*----------------------------------------------------------------------------------------
 * Interface de configuration pour un filtre multi-select
 ----------------------------------------------------------------------------------------*/
export interface MultiSelectFilterConfig<T> {
    property: string;                           // Nom de la propriété pour le filtre (ex: 'partner', 'status', 'type')
    getSelectedItems: () => T[];                // Fonction pour obtenir les éléments sélectionnés
    setSelectedItems: (items: T[]) => void;     // Fonction pour définir les éléments sélectionnés
    allItems: T[];                             // Tableau de tous les éléments disponibles
    valueExtractor: (item: T) => any;          // Fonction pour extraire la valeur à utiliser dans le filtre
    matcher: (item: T, value: any) => boolean; // Fonction pour matcher un item avec une valeur du filtre
}

/*----------------------------------------------------------------------------------------
 * Interface de configuration pour un filtre numérique
 ----------------------------------------------------------------------------------------*/
export interface NumericFilterConfig {
    property: string;                    // Nom de la propriété pour le filtre (ex: 'grossAmount', 'totalAmount')
    getFilter: () => Filter | null;      // Fonction pour obtenir le filtre
    setFilter: (filter: Filter | null) => void; // Fonction pour définir le filtre
}

/*----------------------------------------------------------------------------------------
 * Interface de configuration pour un filtre de texte
 ----------------------------------------------------------------------------------------*/
export interface TextFilterConfig {
    property: string;                    // Nom de la propriété pour le filtre (ex: 'reference', 'label')
    getValue: () => string;              // Fonction pour obtenir la valeur du filtre
    setValue: (value: string) => void;   // Fonction pour définir la valeur du filtre
    subject: Subject<void>;              // Subject pour le debounce
}

/*----------------------------------------------------------------------------------------
 * Interface de configuration pour un filtre checkbox
 ----------------------------------------------------------------------------------------*/
export interface CheckboxFilterConfig {
    property: string;                    // Nom de la propriété pour le filtre (ex: 'hasSettlement', 'appliedRule')
    getValue: () => boolean;             // Fonction pour obtenir l'état de la checkbox
    setValue: (value: boolean) => void;  // Fonction pour définir l'état de la checkbox
    filterType: 'boolean' | 'notNull';   // Type de filtre : 'boolean' pour true/false, 'notNull' pour is not null
}

/*----------------------------------------------------------------------------------------
 * Interface de configuration pour les exclusions d'un filtre multi-select
 ----------------------------------------------------------------------------------------*/
export interface MultiSelectExclusionConfig {
    filterKey: string;                   // La clé du multi-select dans multiSelectFilterConfigs (ex: 'status', 'type')
    property: string;                    // La propriété à filtrer (ex: 'status', 'type/code')
    exclusions: {
        value: any;                      // La valeur qui déclenche l'exclusion
        exclude: any[];                  // Les valeurs à exclure
    }[];
}

@Component({
    selector: 'biifor-table-base',
    template: `
        NO UI TO BE FOUND HERE!
    `,
    standalone: false
})
export class TableFiltersBaseComponent extends BaseComponent {

    /*----------------------------------------------------------------------------------------
     * Configuration des filtres
     *
     * Ce composant abstrait fournit un système de filtrage générique basé sur 4 types :
     * - Multi-select : filtres avec sélection multiple dans une liste (ex: fournisseurs, statuts)
     * - Date range : filtres sur une plage de dates (ex: date de facture, date d'échéance)
     * - Text : filtres textuels avec debounce (ex: référence, libellé)
     * - Numeric : filtres numériques avec opérateurs (ex: montant, quantité)
     *
     * POUR UTILISER CE COMPOSANT DE BASE :
     * 1. Étendre TableFiltersBaseComponent dans votre composant
     * 2. Surcharger les getters dateRangeFilterConfigs, multiSelectFilterConfigs, textFilterConfigs, numericFilterConfigs
     * 3. Implémenter loadData() pour charger les données des listes
     * 4. Créer le template HTML avec les composants PrimeNG appropriés
     *
     * POUR AJOUTER UN NOUVEAU FILTRE MULTI-SELECT :
     * 1. Ajouter une option dans filteringOptions (label pour l'UI, value pour la clé)
     * 2. Dans le composant enfant, déclarer 3 propriétés :
     *    - _items: T[] (tableau privé des items disponibles)
     *    - itemsLoading: boolean (indicateur de chargement)
     *    - itemsSelected: T[] (items sélectionnés)
     * 3. Créer un getter/setter pour _items qui appelle defineSelectedFromMultiSelectFilters()
     * 4. Ajouter une entrée dans multiSelectFilterConfigs avec :
     *    - property: nom de la propriété pour le filtre backend (ex: 'partner')
     *    - getSelectedItems: () => this.itemsSelected
     *    - setSelectedItems: (items) => this.itemsSelected = items
     *    - allItems: this.items (tous les items disponibles)
     *    - valueExtractor: (item) => item.id (valeur à envoyer au backend)
     *    - matcher: (item, value) => item.id === value (pour retrouver l'item depuis l'URL)
     * 5. Dans le template, ajouter un p-multi-select avec :
     *    - @if (showFilter('filterKey'))
     *    - [(ngModel)]="itemsSelected"
     *    - (onChange)="buildFilters()"
     *    - #filterKeyMultiSelect (référence de template, pour vider la recherche à la croix)
     *    - [resetFilterOnHide]="true" (vide la recherche à la fermeture du panneau)
     *    - (onClear)="onClearMultiSelectFilter('filterKey', filterKeyMultiSelect)"
     * 6. Charger les données dans loadData() via le service approprié
     *
     * POUR AJOUTER UN NOUVEAU FILTRE DE PLAGE DE DATES :
     * 1. Ajouter une option dans filteringOptions
     * 2. Dans le composant enfant, déclarer 2 propriétés :
     *    - dateMax?: Date (date maximale optionnelle)
     *    - datesRange: [Date | null, Date | null] | null
     * 3. Ajouter une entrée dans dateRangeFilterConfigs avec :
     *    - property: nom de la propriété pour le filtre backend (ex: 'issueDate')
     *    - getRange: () => this.datesRange
     *    - setRange: (range) => this.datesRange = range
     * 4. Dans le template, ajouter un p-datepicker avec :
     *    - @if (showFilter('filterKey'))
     *    - [(ngModel)]="datesRange"
     *    - selectionMode="range"
     *    - (onSelect)="onSelectDateRange('filterKey')"
     *    - (onClearClick)="onClearDateRange('filterKey')"
     *    - (onClickOutside)="onSelectDateRange('filterKey')" (pour gérer la sélection partielle)
     *
     * POUR AJOUTER UN NOUVEAU FILTRE TEXTE :
     * 1. Ajouter une option dans filteringOptions
     * 2. Dans le composant enfant, déclarer 2 propriétés :
     *    - textFilterValue: string = ''
     *    - textFilterSubject = new Subject<void>() (privé)
     * 3. Ajouter une entrée dans textFilterConfigs avec :
     *    - property: nom de la propriété pour le filtre backend (ex: 'reference')
     *    - getValue: () => this.textFilterValue
     *    - setValue: (value) => this.textFilterValue = value
     *    - subject: this.textFilterSubject
     * 4. Dans le template, ajouter un input avec :
     *    - @if (showFilter('filterKey'))
     *    - [(ngModel)]="textFilterValue"
     *    - (ngModelChange)="onChangeTextFilter('filterKey')"
     * 5. Optionnel : ajouter un bouton clear avec :
     *    - @if (textFilterValue)
     *    - (click)="onClearTextFilter('filterKey')"
     *
     * POUR AJOUTER UN NOUVEAU FILTRE NUMÉRIQUE :
     * 1. Ajouter une option dans filteringOptions
     * 2. Dans le composant enfant, déclarer 1 propriété :
     *    - numericFilter: Filter | null = null
     * 3. Ajouter une entrée dans numericFilterConfigs avec :
     *    - property: nom de la propriété pour le filtre backend (ex: 'grossAmount')
     *    - getFilter: () => this.numericFilter
     *    - setFilter: (filter) => this.numericFilter = filter
     * 4. Dans le template, utiliser le composant biifor-numeric-filter :
     *    - @if (showFilter('filterKey'))
     *    - [filter]="numericFilter"
     *    - (filterChange)="numericFilter = $event; onChangeNumericFilter('filterKey')"
     *    - (clear)="numericFilter = null; onChangeNumericFilter('filterKey')"
     *    - [mode]="'currency'" ou [mode]="'decimal'" selon le type de données
     *
     * POUR AJOUTER UN NOUVEAU FILTRE CHECKBOX :
     * 1. Ajouter une option dans filteringOptions
     * 2. Dans le composant enfant, déclarer 1 propriété :
     *    - checkboxValue: boolean = false
     * 3. Ajouter une entrée dans checkboxFilterConfigs avec :
     *    - property: nom de la propriété pour le filtre backend (ex: 'hasSettlement', 'appliedRule')
     *    - getValue: () => this.checkboxValue
     *    - setValue: (value) => this.checkboxValue = value
     *    - filterType: 'boolean' (filtre sur true) ou 'notNull' (filtre sur not null)
     * 4. Dans le template, ajouter une checkbox avec :
     *    - @if (showFilter('filterKey'))
     *    - [(ngModel)]="checkboxValue"
     *    - (ngModelChange)="buildFilters()"
     * Note : Si checkbox non cochée = pas de filtre (pas de restriction)
     *
     * COMPORTEMENTS AUTOMATIQUES :
     * - Debounce de 1 seconde sur tous les filtres texte
     * - Synchronisation bidirectionnelle avec l'URL (query param $filter)
     * - Construction automatique des filtres OData
     * - Nettoyage automatique des Subjects au ngOnDestroy
     * - Comparateurs : EQ pour multi-select, GE/LE pour dates, Contains pour texte, EQ/NE/GT/GE/LT/LE pour numériques, EQ true pour checkbox boolean, NE null pour checkbox notNull
     ----------------------------------------------------------------------------------------*/

    protected get dateRangeFilterConfigs(): Map<string, DateRangeFilterConfig> {
        return new Map([]);
    }

    protected get multiSelectFilterConfigs(): Map<string, MultiSelectFilterConfig<any>> {
        return new Map([]);
    }

    protected get textFilterConfigs(): Map<string, TextFilterConfig> {
        return new Map([]);
    }

    protected get numericFilterConfigs(): Map<string, NumericFilterConfig> {
        return new Map([]);
    }

    protected get checkboxFilterConfigs(): Map<string, CheckboxFilterConfig> {
        return new Map([]);
    }

    protected get multiSelectExclusionConfigs(): MultiSelectExclusionConfig[] {
        return [];
    }

    /*----------------------------------------------------------------------------------------
     * Données
     ----------------------------------------------------------------------------------------*/

    // Filtres provenant de l'URL (temporaires, ne sont pas sauvegardés dans le tableState)
    private _urlFilterKeys: string[] = [];

    // Filtres choisis par l'utilisateur (sauvegardés dans le tableState du parent)
    private _selectedFilteringOptions: string[] = [];
    // Getter/Setter pour selectedFilteringOptions avec fusion intelligente URL + tableState
    @Input()
    public set selectedFilteringOptions(value: any[]) {
        this._selectedFilteringOptions = [...new Set([...this._urlFilterKeys, ...value || []])];
        // Reset URL filter keys after merging
        this._urlFilterKeys = [];
    }

    public get selectedFilteringOptions(): any[] {
        return this._selectedFilteringOptions;
    }

    @Output() public selectedFilteringOptionsChange = new EventEmitter<any[]>();

    private _previousSelectedFilteringOptions: string[] = [];

    // Objets de filtre actifs, récupérés depuis l'URL, ou servant à construire l'URL
    private _filters: Filter[] = [];
    public get filters(): Filter[] {
        return this._filters;
    }
    public set filters(value: Filter[]) {
        let emptyArray: Filter[] = [];
        this._filters = emptyArray.concat(value);
    }

    // Clés des filtres effectivement renseignés, recalculées à chaque changement d'état
    // (et non à chaque cycle de détection : les getters de config allouent une Map)
    private _activeFilterKeys = new Set<string>();

    /*----------------------------------------------------------------------------------------
     * Accesseurs
     ----------------------------------------------------------------------------------------*/

    public get activeFilterCount(): number {
        return this._activeFilterKeys.size;
    }

    /*----------------------------------------------------------------------------------------
     * Cycle de vie
     ----------------------------------------------------------------------------------------*/

    public override ngOnInit(): void {
        super.ngOnInit();

        // Écouter les changements des filtres texte avec debounce de 1 seconde
        this.textFilterConfigs.forEach((config, key) => {
            this.subscriptions.add(
                config.subject.pipe(
                    debounceTime(1000)
                ).subscribe(() => {
                    this._buildFilters();
                })
            );
        });
    }

    public override ngOnDestroy(): void {
        super.ngOnDestroy();
        // Nettoyer les subjects des filtres texte
        this.textFilterConfigs.forEach((config, key) => {
            config.subject.complete();
        });
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes publiques
     ----------------------------------------------------------------------------------------*/

    public buildFilters(): void {
        this._buildFilters();
    }

    /**
     * Indique si un filtre est renseigné (et donc restreint les résultats).
     * Un filtre affiché mais vide n'est pas considéré comme actif.
     */
    public isFilterActive(filterKey: string): boolean {
        return this._activeFilterKeys.has(filterKey);
    }

    protected onChangeTextFilter(textFilterKey: string): void {
        const config = this.textFilterConfigs.get(textFilterKey);
        if (config) {
            config.subject.next();
        }
    }

    protected onClearDateRange(dateRangeKey: string): void {
        const config = this.dateRangeFilterConfigs.get(dateRangeKey);
        if (config) {
            config.setRange(null);
        }
        this._buildFilters();
    }

    public onClearMultiSelectFilter(filterKey: string, multiSelect?: MultiSelect): void {
        const config = this.multiSelectFilterConfigs.get(filterKey);
        if (config) {
            config.setSelectedItems([]);
        }
        multiSelect?.resetFilter();
        this._buildFilters();
    }

    public onClearTextFilter(textFilterKey: string): void {
        const config = this.textFilterConfigs.get(textFilterKey);
        if (config) {
            config.setValue('');
        }
        this._buildFilters();
    }

    public onClearNumericFilter(numericFilterKey: string): void {
        const config = this.numericFilterConfigs.get(numericFilterKey);
        if (config) {
            config.setFilter(null);
        }
        this._buildFilters();
    }

    public onClickResetFilters(): void {
        // Réinitialiser tous les filtres multi-select
        this.multiSelectFilterConfigs.forEach((config, key) => {
            config.setSelectedItems([]);
        });

        // Réinitialiser toutes les plages de dates
        this.dateRangeFilterConfigs.forEach((config, key) => {
            config.setRange(null);
        });

        // Réinitialiser tous les filtres texte
        this.textFilterConfigs.forEach((config, key) => {
            config.setValue('');
        });

        // Réinitialiser tous les filtres numériques
        this.numericFilterConfigs.forEach((config, key) => {
            config.setFilter(null);
        });

        // Réinitialiser tous les filtres checkbox
        this.checkboxFilterConfigs.forEach((config, key) => {
            config.setValue(false);
        });

        // Vider les filtres et mettre à jour l'URL
        this._filters = [];
        this._computeActiveFilterKeys();
        this._updateURLWithFilters();
    }

    /**
     * Called when user changes the selectedFilteringOptions (adds/removes filters from the dropdown)
     */
    public onFilteringOptionsChange(): void {
        const currentSelection = [...this.selectedFilteringOptions];

        // Delegate to base component to handle clearing removed filters
        this.onSelectedFilteringOptionsChange(currentSelection, this._previousSelectedFilteringOptions);

        // Update previous selection for next change
        this._previousSelectedFilteringOptions = currentSelection;

        // Extract only user-selected filters (exclude remaining URL filters) for tableState saving
        const userSelectedFilters = currentSelection.filter(key => !this._urlFilterKeys.includes(key));

        // Update internal state
        this._selectedFilteringOptions = userSelectedFilters;

        // Emit ONLY user-selected filters to parent (URL filters are not saved in tableState)
        this.selectedFilteringOptionsChange.emit(userSelectedFilters);
    }

    public onSelectDateRange(dateRangeKey: string): void {
        const config = this.dateRangeFilterConfigs.get(dateRangeKey);
        if (!config) return;

        const range = config.getRange();
        // Vérifie qu'au moins une date est sélectionnée
        if (!range || range.filter(date => date !== null).length < 1) {
            return;
        }
        this._buildFilters();
    }

    public showFilter(filteringOptionValue: string): boolean {
        return this.selectedFilteringOptions.some(option => option === filteringOptionValue);
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes protégées
     ----------------------------------------------------------------------------------------*/

    /**
     * Point d'extension pour les filtres custom
     * À surcharger dans les composants enfants pour ajouter des filtres spécifiques
     * Cette méthode est appelée après la construction des filtres standard et avant la mise à jour de l'URL
     */
    protected buildCustomFilters(): void {
        // Vide par défaut - à surcharger si nécessaire
    }

    /**
     * Définition générique des éléments sélectionnés depuis les filtres URL
     */
    protected defineSelectedFromMultiSelectFilters<T>(configKey: string): void {
        const config = this.multiSelectFilterConfigs.get(configKey);
        if (config) {
            const values: any[] = [];
            this._extractValuesFromFilters(this._filters, config.property, values);

            if (values.length > 0) {
                const selectedItems: T[] = [];
                values.forEach(value => {
                    const item = config.allItems.find(i => config.matcher(i, value));
                    if (item) {
                        selectedItems.push(item);
                    }
                });
                config.setSelectedItems(selectedItems);
                this._computeActiveFilterKeys();
                return;
            }
        }
        config?.setSelectedItems([]);
        this._computeActiveFilterKeys();
    }

    protected override mapParams(): void {
        this._buildFiltersFromQueryParams();
        this.dateRangeFilterConfigs.forEach((config, key) => {
            this._defineDateRangeFromFilters(key);
        });
        this.textFilterConfigs.forEach((config, key) => {
            this._defineTextFilterFromFilters(config);
        });
        this.numericFilterConfigs.forEach((config, key) => {
            this._defineNumericFilterFromFilters(key);
        });
        this.checkboxFilterConfigs.forEach((config, key) => {
            this._defineCheckboxFilterFromFilters(config);
        });

        // Mise à jour des clés de filtres actifs pour la mise en évidence
        this._computeActiveFilterKeys();

        // Store URL filter keys separately (not saved in tableState)
        this._urlFilterKeys = this._getActiveFilterKeys();

        // Initialize tracking with the full merged list (URL + tableState)
        this._previousSelectedFilteringOptions = [...this.selectedFilteringOptions];
    }

    /**
     * Handle changes to selectedFilteringOptions
     * Clear filters when they are removed from the selection
     */
    protected onSelectedFilteringOptionsChange(newSelection: string[], previousSelection: string[]): void {
        // Find removed keys
        const removedKeys = previousSelection.filter(key => !newSelection.includes(key));

        // Clear each removed filter
        for (const key of removedKeys) {
            const filterType = this._getFilterType(key);

            switch (filterType) {
                case 'multiselect':
                    this.onClearMultiSelectFilter(key);
                    break;
                case 'daterange':
                    this.onClearDateRange(key);
                    break;
                case 'text':
                    this.onClearTextFilter(key);
                    break;
                case 'numeric':
                    this.onClearNumericFilter(key);
                    break;
            }
        }
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes privées
     ----------------------------------------------------------------------------------------*/

    private _buildFilters() {
        // Réinitialisation des filtres
        this._filters = [];

        // Construction des filtres pour les listes de valeurs configurées
        this.multiSelectFilterConfigs.forEach((config, key) => {
            this._buildFiltersForMultiSelectProperty(config);
        });
        // Construction des filtres pour les plages de dates
        this.dateRangeFilterConfigs.forEach((config, key) => {
            this._buildFiltersForDateRange(config);
        });
        // Construction des filtres texte
        this.textFilterConfigs.forEach((config, key) => {
            this._buildFilterForText(config);
        });
        // Construction des filtres numériques
        this.numericFilterConfigs.forEach((config, key) => {
            this._buildFilterForNumeric(config);
        });
        // Construction des filtres checkbox
        this.checkboxFilterConfigs.forEach((config, key) => {
            this._buildFilterForCheckbox(config);
        });

        // Application des exclusions de multi-select configurées
        this._applyMultiSelectExclusions();

        // Point d'extension pour les filtres custom des composants enfants
        this.buildCustomFilters();

        // Mise à jour des clés de filtres actifs pour la mise en évidence
        this._computeActiveFilterKeys();

        // Mise à jour de l'URL avec les nouveaux filtres
        this._updateURLWithFilters();
    }

    /**
     * Construction générique d'un filtre texte
     */
    private _buildFilterForText(config: TextFilterConfig): void {
        const value = config.getValue().trim();
        if (value.length > 0) {
            this._filters.push(new Filter({
                property: config.property,
                comparator: Filter.COMPARATOR.Contains,
                value: value
            }));
        }
    }

    /**
     * Construction générique d'un filtre numérique
     */
    private _buildFilterForNumeric(config: NumericFilterConfig): void {
        const filter = config.getFilter();
        if (filter) {
            this._filters.push(filter);
        }
    }

    /**
     * Construction générique d'un filtre checkbox
     * - 'boolean': ajoute un filtre EQ true si la checkbox est cochée
     * - 'notNull': ajoute un filtre NE null si la checkbox est cochée
     */
    private _buildFilterForCheckbox(config: CheckboxFilterConfig): void {
        const isChecked = config.getValue();
        if (isChecked) {
            if (config.filterType === 'boolean') {
                // Pour un booléen : filtre sur true
                this._filters.push(new Filter({
                    property: config.property,
                    comparator: Filter.COMPARATOR.EQ,
                    value: true
                }));
            } else if (config.filterType === 'notNull') {
                // Pour notNull : filtre sur not equals null
                this._filters.push(new Filter({
                    property: config.property,
                    comparator: Filter.COMPARATOR.NE,
                    value: null
                }));
            }
        }
        // Si la checkbox n'est pas cochée, on n'ajoute aucun filtre (pas de restriction)
    }

    /**
     * Construction générique des filtres pour une plage de dates
     */
    private _buildFiltersForDateRange(config: DateRangeFilterConfig): void {
        const range = config.getRange();
        if (range && range.length === 2) {
            const startDate = range[0];
            const endDate = range[1];

            const filters: Filter[] = [];

            if (startDate) {
                filters.push(new Filter({
                    property: config.property,
                    comparator: Filter.COMPARATOR.GE,
                    value: this._formatDateToLocal(startDate)
                }));
            }

            if (endDate) {
                filters.push(new Filter({
                    property: config.property,
                    comparator: Filter.COMPARATOR.LE,
                    value: this._formatDateToLocal(endDate)
                }));
            }

            if (filters.length > 0) {
                const combinedFilter = new Filter({
                    filters: filters,
                    and: true
                });
                this._filters.push(combinedFilter);
            }
        }
    }

    /**
     * Construction générique des filtres pour une propriété multi-select
     */
    private _buildFiltersForMultiSelectProperty<T>(config: MultiSelectFilterConfig<T>): void {
        const selectedItems = config.getSelectedItems();
        if (selectedItems.length > 0) {
            const filters: Filter[] = selectedItems.map((item: T) => {
                return new Filter({
                    property: config.property,
                    comparator: Filter.COMPARATOR.EQ,
                    value: config.valueExtractor(item)
                });
            });

            const combinedFilter = new Filter({
                filters: filters,
                and: false
            });

            this._filters.push(combinedFilter);
        }
    }

    private _buildFiltersFromQueryParams() {
        this.filters = Filter.fromQueryParams(this.queryParams);
    }

    /**
     * Recalcule les clés des filtres renseignés depuis les accesseurs de configuration.
     * On part des configs et non des Filter produits, car un filtre custom peut mapper
     * une clé sur plusieurs propriétés (ex: 'period' -> periodStart + periodEnd).
     */
    private _computeActiveFilterKeys(): void {
        const activeKeys = new Set<string>();

        this.multiSelectFilterConfigs.forEach((config, key) => {
            if (config.getSelectedItems().length > 0) {
                activeKeys.add(key);
            }
        });

        this.dateRangeFilterConfigs.forEach((config, key) => {
            const range = config.getRange();
            if (range && (range[0] || range[1])) {
                activeKeys.add(key);
            }
        });

        this.textFilterConfigs.forEach((config, key) => {
            if (config.getValue().trim().length > 0) {
                activeKeys.add(key);
            }
        });

        this.numericFilterConfigs.forEach((config, key) => {
            if (config.getFilter()) {
                activeKeys.add(key);
            }
        });

        this.checkboxFilterConfigs.forEach((config, key) => {
            if (config.getValue()) {
                activeKeys.add(key);
            }
        });

        this._activeFilterKeys = activeKeys;
    }

    /**
     * Définition générique d'une plage de dates depuis les filtres URL
     */
    private _defineDateRangeFromFilters(dateRangeKey: string): void {
        const config = this.dateRangeFilterConfigs.get(dateRangeKey);
        if (config) {
            config.setRange(this._extractDateRangeFromFilters(config.property));
        }
    }

    /**
     * Définition générique d'un filtre numérique depuis les filtres URL
     */
    private _defineNumericFilterFromFilters(configKey: string): void {
        const config = this.numericFilterConfigs.get(configKey);
        if (!config) return;

        // Chercher le filtre correspondant à cette propriété
        const filter = this._findNumericFilter(this._filters, config.property);

        if (filter) {
            config.setFilter(filter);
        } else {
            config.setFilter(null);
        }
    }

    /**
     * Définition générique d'un filtre texte depuis les filtres URL
     */
    private _defineTextFilterFromFilters(config: TextFilterConfig): void {
        const filter = this._filters.find(f => f.property === config.property);

        if (filter && filter.value) {
            config.setValue(filter.value);
        } else {
            config.setValue('');
        }
    }

    /**
     * Définition générique d'un filtre checkbox depuis les filtres URL
     * - 'boolean': vérifie si le filtre est EQ true
     * - 'notNull': vérifie si le filtre est NE null
     */
    private _defineCheckboxFilterFromFilters(config: CheckboxFilterConfig): void {
        const filter = this._filters.find(f => f.property === config.property);

        if (filter) {
            if (config.filterType === 'boolean' && filter.comparator === Filter.COMPARATOR.EQ && filter.value === true) {
                config.setValue(true);
            } else if (config.filterType === 'notNull' && filter.comparator === Filter.COMPARATOR.NE && filter.value === null) {
                config.setValue(true);
            } else {
                config.setValue(false);
            }
        } else {
            config.setValue(false);
        }
    }

    /**
     * Extraction générique d'une plage de dates depuis les filtres
     */
    private _extractDateRangeFromFilters(property: string): [Date | null, Date | null] | null {
        let startDate: Date | null = null;
        let endDate: Date | null = null;

        this._extractDateRangeValues(this._filters, property, (comparator, value) => {
            if (comparator === Filter.COMPARATOR.GE) {
                startDate = this._parseDateFromString(value);
            } else if (comparator === Filter.COMPARATOR.LE) {
                endDate = this._parseDateFromString(value);
            }
        });

        if (startDate || endDate) {
            return [startDate, endDate];
        }
        return null;
    }

    /**
     * Extraction récursive des valeurs de dates avec leurs comparateurs
     */
    private _extractDateRangeValues(
        filters: Filter[],
        property: string,
        callback: (comparator: string, value: any) => void
    ): void {
        // Normaliser la property pour la comparaison (remplacer / par .)
        const normalizedProperty = property.replace(/\//g, '.');

        for (const filter of filters) {
            if (filter.property) {
                // Normaliser aussi la property du filtre pour la comparaison
                const normalizedFilterProperty = filter.property.replace(/\//g, '.');

                if (normalizedFilterProperty === normalizedProperty && filter.value !== null && filter.value !== undefined) {
                    callback(filter.comparator, filter.value);
                }
            }

            if (filter.filters && filter.filters.length > 0) {
                this._extractDateRangeValues(filter.filters, property, callback);
            }
        }
    }

    /**
     * Extraction générique et récursive des valeurs depuis les filtres
     * Supporte les propriétés avec notation OData (type/code) et notation pointée (type.code)
     * Ignore les filtres NE (NotEqual) car ils représentent des exclusions, pas des sélections
     */
    private _extractValuesFromFilters(filters: Filter[], property: string, values: any[]): void {
        // Normaliser la property pour la comparaison (remplacer / par .)
        const normalizedProperty = property.replace(/\//g, '.');

        for (const filter of filters) {
            if (filter.property) {
                // Normaliser aussi la property du filtre pour la comparaison
                const normalizedFilterProperty = filter.property.replace(/\//g, '.');

                if (normalizedFilterProperty === normalizedProperty && filter.value !== null && filter.value !== undefined) {
                    // Ignorer les filtres NE (NotEqual) car ils représentent des exclusions
                    if (filter.comparator === Filter.COMPARATOR.NE) {
                        continue;
                    }

                    // C'est un filtre simple sur la propriété
                    if (!values.includes(filter.value)) {
                        values.push(filter.value);
                    }
                }
            }

            if (filter.filters && filter.filters.length > 0) {
                // C'est un filtre composite, on parcourt récursivement
                this._extractValuesFromFilters(filter.filters, property, values);
            }
        }
    }

    /**
     * Applique les exclusions configurées pour les filtres multi-select
     * Pour chaque configuration d'exclusion, si la valeur déclenchante est sélectionnée,
     * les valeurs à exclure sont ajoutées comme filtres NE (not equals),
     * SAUF si ces valeurs sont explicitement sélectionnées par l'utilisateur
     */
    private _applyMultiSelectExclusions(): void {
        // Pour chaque configuration d'exclusion
        for (const exclusionConfig of this.multiSelectExclusionConfigs) {
            const filterConfig = this.multiSelectFilterConfigs.get(exclusionConfig.filterKey);
            if (!filterConfig) continue;

            const selectedItems = filterConfig.getSelectedItems();
            if (selectedItems.length === 0) continue;

            // Extraire les valeurs sélectionnées
            const selectedValues = selectedItems.map(item =>
                filterConfig.valueExtractor(item)
            );

            // Pour chaque règle d'exclusion
            for (const rule of exclusionConfig.exclusions) {
                // Si la valeur avec exclusions est sélectionnée
                if (selectedValues.includes(rule.value)) {
                    // Parcourir les valeurs à exclure
                    for (const excludedValue of rule.exclude) {
                        // Exclure uniquement si la valeur n'est PAS explicitement sélectionnée
                        if (!selectedValues.includes(excludedValue)) {
                            this._filters.push(new Filter({
                                property: exclusionConfig.property,
                                comparator: Filter.COMPARATOR.NE,
                                value: excludedValue
                            }));
                        }
                    }
                }
            }
        }
    }

    /**
     * Parse une date depuis une string YYYY-MM-DD en créant une date locale
     * (évite les problèmes de timezone avec new Date('YYYY-MM-DD'))
     */
    private _parseDateFromString(dateString: string): Date {
        const parts = dateString.split('-');
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1; // Les mois commencent à 0 en JavaScript
        const day = parseInt(parts[2], 10);
        return new Date(year, month, day);
    }

    /**
     * Formate une date en chaîne locale (YYYY-MM-DD) sans conversion UTC
     */
    private _formatDateToLocal(date: Date): string {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    /**
     * Find config key for a given filter property
     * Searches across all 4 filter type configurations
     */
    private _findConfigKeyForProperty(property: string): string | null {
        // Search in multi-select configs
        for (const [key, config] of this.multiSelectFilterConfigs) {
            if (config.property === property) {
                return key;
            }
        }

        // Search in date range configs
        for (const [key, config] of this.dateRangeFilterConfigs) {
            if (config.property === property) {
                return key;
            }
        }

        // Search in text configs
        for (const [key, config] of this.textFilterConfigs) {
            if (config.property === property) {
                return key;
            }
        }

        // Search in numeric configs
        for (const [key, config] of this.numericFilterConfigs) {
            if (config.property === property) {
                return key;
            }
        }

        return null;
    }

    /**
     * Recherche récursive d'un filtre numérique (simple ou BETWEEN)
     */
    private _findNumericFilter(filters: Filter[], property: string): Filter | null {
        for (const filter of filters) {
            // Cas 1 : Filtre simple (EQ, NE, GT, GE, LT, LE)
            if (filter.property === property && filter.value !== null && filter.value !== undefined) {
                return filter;
            }

            // Cas 2 : Filtre BETWEEN (AND avec 2 filtres GE et LE)
            if (filter.and && filter.filters && filter.filters.length === 2) {
                const hasGE = filter.filters.some(f => f.property === property && f.comparator === Filter.COMPARATOR.GE);
                const hasLE = filter.filters.some(f => f.property === property && f.comparator === Filter.COMPARATOR.LE);

                if (hasGE && hasLE) {
                    return filter;
                }
            }

            // Cas 3 : Recherche récursive dans les sous-filtres
            if (filter.filters && filter.filters.length > 0) {
                const found = this._findNumericFilter(filter.filters, property);
                if (found) return found;
            }
        }

        return null;
    }

    /**
     * Extract active filter keys from _filters array
     * Maps filter properties to their corresponding config keys
     */
    private _getActiveFilterKeys(): string[] {
        const activeKeys: string[] = [];
        const processedProperties = new Set<string>();

        // Extract unique properties from all filters (recursively)
        const extractProperties = (filters: Filter[]) => {
            for (const filter of filters) {
                if (filter.property && !processedProperties.has(filter.property)) {
                    processedProperties.add(filter.property);

                    // Find matching config key for this property
                    const configKey = this._findConfigKeyForProperty(filter.property);
                    if (configKey && !activeKeys.includes(configKey)) {
                        activeKeys.push(configKey);
                    }
                }

                // Process nested filters
                if (filter.filters && filter.filters.length > 0) {
                    extractProperties(filter.filters);
                }
            }
        };

        extractProperties(this._filters);
        return activeKeys;
    }

    /**
     * Determine filter type from config key
     */
    private _getFilterType(key: string): 'multiselect' | 'daterange' | 'text' | 'numeric' | null {
        if (this.multiSelectFilterConfigs.has(key)) return 'multiselect';
        if (this.dateRangeFilterConfigs.has(key)) return 'daterange';
        if (this.textFilterConfigs.has(key)) return 'text';
        if (this.numericFilterConfigs.has(key)) return 'numeric';
        return null;
    }

    /**
     * Mise à jour du query param $filter dans l'URL en fonction des filtres actifs
     */
    private _updateURLWithFilters(): void {
        const params: { [key: string]: any } = {};

        // Copie de tous les paramètres existants sauf $filter et $skip
        this.queryParams.keys.forEach(key => {
            if (key !== '$filter' && key !== '$skip') {
                params[key] = this.queryParams.get(key);
            }
        });

        // Réinitialiser la pagination à la première page quand les filtres changent
        params['$skip'] = 0;

        // Mise à jour du paramètre $filter avec les filtres du composant
        if (this._filters.length > 0) {
            params['$filter'] = Filter.toUrlString(this._filters, false);
        }

        // Navigation vers l'URL mise à jour
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: params
        });
    }

}
